import {
  SPIDERFY_CIRCLE_FOOT_SEPARATION,
  SPIDERFY_CIRCLE_LIMIT,
  SPIDERFY_SPIRAL_FOOT_SEPARATION,
  SPIDERFY_SPIRAL_LENGTH_FACTOR,
  SPIDERFY_SPIRAL_LENGTH_START,
} from "./mapConstants";

interface PixelOffset {
  x: number;
  y: number;
}

/** Evenly spaced points on a circle whose radius grows with the count. */
const circleOffsets = (count: number): PixelOffset[] => {
  const legLength = (SPIDERFY_CIRCLE_FOOT_SEPARATION * (2 + count)) / (2 * Math.PI);
  const angleStep = (2 * Math.PI) / count;

  return Array.from({ length: count }, (_, index) => {
    const angle = index * angleStep;
    return {
      x: legLength * Math.cos(angle),
      y: legLength * Math.sin(angle),
    };
  });
};

/** Archimedean spiral, so large groups stay separated without a huge radius. */
const spiralOffsets = (count: number): PixelOffset[] => {
  let legLength = SPIDERFY_SPIRAL_LENGTH_START;
  let angle = 0;

  return Array.from({ length: count }, (_, index) => {
    angle += SPIDERFY_SPIRAL_FOOT_SEPARATION / legLength + index * 0.0005;
    const offset = {
      x: legLength * Math.cos(angle),
      y: legLength * Math.sin(angle),
    };
    legLength += (2 * Math.PI * SPIDERFY_SPIRAL_LENGTH_FACTOR) / angle;
    return offset;
  });
};

/**
 * Pixel offsets, relative to the cluster centre, at which overlapping pins are
 * fanned out so each one becomes individually clickable.
 */
export const getSpiderfyOffsets = (count: number): PixelOffset[] => {
  if (count <= 0) return [];
  return count <= SPIDERFY_CIRCLE_LIMIT ? circleOffsets(count) : spiralOffsets(count);
};

/**
 * Shift a coordinate by a screen-pixel offset at the map's current zoom.
 * Returns `null` while the map projection is not ready yet.
 */
export const offsetLatLngByPixels = (
  map: google.maps.Map,
  origin: google.maps.LatLng,
  offset: PixelOffset
): google.maps.LatLng | null => {
  const projection = map.getProjection();
  const zoom = map.getZoom();

  if (!projection || zoom === undefined) return null;

  const originPoint = projection.fromLatLngToPoint(origin);
  if (!originPoint) return null;

  const scale = 2 ** zoom;
  const shifted = new google.maps.Point(
    originPoint.x + offset.x / scale,
    originPoint.y + offset.y / scale
  );

  return projection.fromPointToLatLng(shifted);
};
